import { ref } from 'vue'

const STORAGE_KEY = 'beads-console-recent-rejection-reasons'
const MAX_RECENT = 8

/** Common bulk-cleanup reasons offered as autocomplete suggestions. */
export const PRESET_REJECTION_REASONS = [
  'Stale — no longer relevant',
  'Duplicate plan',
  'Obsolete by newer plan',
  'Worker offline at plan time',
]

function readStored() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((entry) => typeof entry === 'string' && entry.trim())
      .slice(0, MAX_RECENT)
  } catch {
    // Recent reasons are a best-effort UI convenience cache, not product
    // data. If localStorage is unavailable/corrupt (private mode, quota,
    // bad JSON) we fall back to presets only — nothing is lost.
    return []
  }
}

function writeStored(reasons) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(reasons))
  } catch {
    // Best-effort only — see readStored(). A failed write just means the next
    // session won't see this suggestion; it is not a data-loss path.
  }
}

/**
 * Manage rejection-reason suggestions for the Plans page: static presets plus
 * the operator's most-recently-used reasons, persisted client-side so a
 * backlog cleanup doesn't require retyping the same reason for every plan.
 */
export function useRejectionReasons() {
  const recentReasons = ref(readStored())

  function recordReason(reason) {
    const trimmed = typeof reason === 'string' ? reason.trim() : ''
    if (!trimmed) return
    const next = [trimmed, ...recentReasons.value.filter((entry) => entry !== trimmed)].slice(0, MAX_RECENT)
    recentReasons.value = next
    writeStored(next)
  }

  return {
    presetReasons: PRESET_REJECTION_REASONS,
    recentReasons,
    recordReason,
  }
}
